import { useState } from "react";
import { Outlet } from "react-router";
import { useDismissable } from "@/lib/useDismissable";
import { ServiceStatusBanner } from "./ServiceStatusBanner";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

const SIDEBAR_EXPANDED_KEY = "tendra.sidebar_expanded";

function readSidebarExpanded() {
  try {
    return localStorage.getItem(SIDEBAR_EXPANDED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeSidebarExpanded(expanded: boolean) {
  try {
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(expanded));
  } catch {
    // Storage may be unavailable, in which case the sidebar starts collapsed.
  }
}

export function AppLayout() {
  const [sidebarExpanded, setSidebarExpanded] = useState(readSidebarExpanded);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useDismissable<HTMLDivElement>(menuOpen, () =>
    setMenuOpen(false),
  );

  return (
    <div className="relative flex h-dvh w-screen flex-col bg-background">
      {/* Safari uses this to tint its title bar. It sits above modals so their backdrops don't take over. It's translucent to keep the menu button visible and hidden while the banner is shown. */}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-4 bg-background/15 [&:has(+[data-service-status-banner])]:hidden" />

      <ServiceStatusBanner />

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div ref={navRef} className="contents">
          <Sidebar
            expanded={sidebarExpanded}
            onToggle={() => {
              writeSidebarExpanded(!sidebarExpanded);
              setSidebarExpanded(!sidebarExpanded);
            }}
            menuOpen={menuOpen}
            onNavigate={() => setMenuOpen(false)}
          />

          <Topbar
            menuOpen={menuOpen}
            onMenuToggle={() => setMenuOpen((v) => !v)}
          />
        </div>

        <main
          className={`absolute bottom-0 left-0 right-0 top-14 overflow-auto rounded-t-3xl bg-base p-4 md:top-4 md:rounded-tr-none md:transition-[left] md:duration-200 ${
            sidebarExpanded ? "md:left-64" : "md:left-16"
          }`}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
